import { Box } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type { Category, CategorySortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import CategoryActions from "@/screens/ManageProducts/components/ProductCategories/components/CategoryActions"
import ProductCategoriesTable from "@/screens/ManageProducts/components/ProductCategories/tables/ProductCategoriesTable"
import type { ProductCategoriesSearchFilters } from "@/screens/ManageProducts/stores/useProductCategoriesSearchStore"
import { useProductCategoriesSearchStore } from "@/screens/ManageProducts/stores/useProductCategoriesSearchStore"
import { toUpperSnakeCase, upperSnakeCaseToCamelCase } from "@/utils/utils"

const ProductCategoriesContainer: FC = () => {
  const { field, order } = useSearch({ from: "/app/admin/manage-products/_layout/categories" })
  const navigate = useNavigate()
  const [isOpenedCategoryFormDialog, setIsOpenedCategoryFormDialog] = useState<boolean>(false)
  const [selectedCategory, setSelectedCategory] = useState<Category | undefined>(undefined)

  const data = useProductCategoriesSearchStore(({ data }) => data)
  const fetching = useProductCategoriesSearchStore(({ fetching }) => fetching)
  const error = useProductCategoriesSearchStore(({ error }) => error)
  const loadMore = useProductCategoriesSearchStore(({ loadMore }) => loadMore)
  const clearStore = useProductCategoriesSearchStore(({ clearStore }) => clearStore)
  const executeSearch = useProductCategoriesSearchStore(({ executeSearch }) => executeSearch)

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const fetchData = useCallback(
    (skipFilterCheck = true) => {
      const searchFilters: ProductCategoriesSearchFilters = {
        field,
        order,
      }
      executeSearch(queryClient, searchFilters, skipFilterCheck)
    },
    [executeSearch, field, order]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  useEffect(() => {
    fetchData(false)
  }, [fetchData])

  const [throttledLoadMore] = useThrottle(() => {
    const searchFilters: ProductCategoriesSearchFilters = {
      field,
      order,
    }
    loadMore(queryClient, searchFilters)
  }, 200)

  const onScrollHandler = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const sorting = useMemo(() => {
    if (!field) return []

    const tableSortField = upperSnakeCaseToCamelCase(field)
    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [field, order])

  const onHandleSortingChange = (updaterFn: (state: SortingState) => SortingState) => {
    const newSorting = updaterFn(sorting)
    if (newSorting.length) {
      const sort = newSorting[0]
      const key = sort.id
      const field = toUpperSnakeCase(key) as CategorySortField
      const params = {
        field,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
      }

      navigate({
        search: params,
        to: "/app/admin/manage-products/categories",
        replace: true,
      })
    }
  }

  return (
    <Box className='flex h-full flex-col gap-4'>
      <CategoryActions
        refreshData={fetchData}
        openedDialog={isOpenedCategoryFormDialog}
        setOpenedDialog={setIsOpenedCategoryFormDialog}
        existingCategory={selectedCategory}
        setExistingCategory={setSelectedCategory}
      />
      <Box className='grow overflow-hidden'>
        <ProductCategoriesTable
          data={data}
          onScrollHandler={onScrollHandler}
          tableContainerRef={tableContainerRef}
          onHandleSortingChange={onHandleSortingChange}
          sorting={sorting}
          fetching={fetching}
          error={error}
          onOpenEditCategoryDialog={(original: Category) => {
            setIsOpenedCategoryFormDialog(true)
            setSelectedCategory(original)
          }}
        />
      </Box>
    </Box>
  )
}

export default ProductCategoriesContainer
